import {
  WmPagination,
  WmTabItem,
  WmTabList,
  WmTabPanel
} from "@watermarkinsights/ripple-react";
import { CompletedTasks } from "./CompletedTasks";
import { useEffect, useContext, useReducer } from "react";
import { sendRequest } from "@/common/http";
import { useIntl } from "react-intl";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import { Tab } from "@/features/lightcast/Interface";
import translate from "@/i18n/translate";
import {
  formatDateWithMonthAbbr,
  scrollToTop
} from "@/features/common/helper";
import { Link, useNavigate } from "react-router-dom";
import WidgetLoader from "@/features/common/elements/WidgetLoader";
import {
  UserGroupIcon,
  SettingsIcon,
  FolderIcon
} from "@/features/common/Icons";
import {
  pageSize,
  isTaskInCurrentPage
} from "@/features/common/tabComponent/InsightsTabComponent";

interface MyTasksState {
  selectedTab: string;
  taskCount: number;
  tasks: Task[] | null;
  loadedTasks: boolean;
  currentPage: number;
}

interface MyTasksAction {
  type: string;
  tabId: string;
  tab: string;
  currentPage: number;
  tasks: Task[] | null;
}

const defaultAction: MyTasksAction = {
  type: "",
  tabId: "",
  tab: "",
  currentPage: 1,
  tasks: []
};
interface Task {
  title: string;
  dueDate: string;
  lastModifyDate: string;
  product: string;
  url: string;
  type: string;
  state: string;
  count: number;
}

const reducer = (state: MyTasksState, action: MyTasksAction) => {
  const currentTaskCount = state.taskCount;
  switch (action.type) {
    case "setSelectedTab":
      return { ...state, selectedTab: action.tabId };

    case "setPage":
      return { ...state, currentPage: action.currentPage };

    case "setTasks":
      const taskCount = action.tasks
        ? currentTaskCount + action.tasks.length
        : currentTaskCount;

      return {
        ...state,
        tasks: action.tasks,
        taskCount: taskCount,
        loadedTasks: true
      };

    default:
      return state;
  }
};

const initialState: MyTasksState = {
  selectedTab: "to-do",
  loadedTasks: false,
  taskCount: 0,
  tasks: null,
  currentPage: 1
};

export function MyTasks() {
  const { institution, sysUser } = useContext<AppContextInterface>(AppContext);
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (institution && institution.salesforceId) {
      const applyResult = (result: Task[]) => {
        dispatch({
          ...defaultAction,
          type: "setTasks",
          tasks: result
        });
      };

      const onError = () => {
        dispatch({
          ...defaultAction,
          type: "setTasks",
          tasks: []
        });
      };

      sendRequest(
        {
          uri: `/home/tasks`,
          camelize: true
        },
        applyResult,
        onError
      );
    }
  }, [institution]);

  const onWmTabSelected = (event: CustomEvent) => {
    dispatch({
      ...defaultAction,
      type: "setSelectedTab",
      tabId: event.detail.tabId
    });
  };

  const navigateToExternalTask = (url: string) => {
    window.location.href = url;
  };
  const handleKeyPressFs = (
    event: React.KeyboardEvent<HTMLDivElement>,
    url: string
  ) => {
    if (event.key === " " || event.key === "Enter") {
      navigateToExternalTask(url);
    }
  };

  const toDoLabel = useIntl().formatMessage(
    {
      id: "app.home.toDoLabel"
    },
    { count: state.loadedTasks && state.taskCount }
  );

  const reportingYearTaskHeading = useIntl().formatMessage({
    id: "app.home.createReportingYear"
  });
  const reportingYearTaskDesc = useIntl().formatMessage({
    id: "app.home.createReportingYearDesc"
  });

  const completedLabel = useIntl().formatMessage({
    id: "app.home.completedLabel"
  });

  const tabOption = [
    {
      name: toDoLabel,
      id: "to-do"
    },
    {
      name: completedLabel,
      id: "completed"
    }
  ];

  function showEmptyState() {
    return state.loadedTasks && state.tasks?.length === 0;
  }

  function showTasks() {
    return state.loadedTasks && state.tasks && state.tasks?.length > 0;
  }

  const setPage = (event: any) => {
    dispatch({
      ...defaultAction,
      type: "setPage",
      currentPage: event.target.value
    });
  };

  return (
    <div className="my-tasks-card paper-card">
      <div className="card-header header">
        <h3 className="my-tasks-heading title">{translate("app.home.myTasks")}</h3>
        <div className="base-font">{translate("app.home.myTasksDesc")}</div>
      </div>
      <div className="body">
        <div className="tabbar">
          <WmTabList
            id="my-tasks-tabs"
            selectedTab={state.selectedTab}
            onWmTabSelected={onWmTabSelected}
          >
            {tabOption.map((tab: Tab, index: number) => (
              <WmTabItem tabId={tab.id} key={tab.id + (index + 1)}>
                {tab.name}
              </WmTabItem>
            ))}
          </WmTabList>
        </div>

        <WmTabPanel className="tab-panel" id="tab-one" tabId={"to-do"}>
          {state.loadedTasks && state.taskCount > 0 && (
            <div className="tab-content-title">
              {translate("app.home.tasksToDo", { count: state.taskCount })}
            </div>
          )}
          <div className="tab-container">

            {showEmptyState() && <ToDoEmptyState />}

            {!state.loadedTasks && (
              <WidgetLoader
                loaderContainerClasses="widget-loader-container"
                loaderClasses="widget-loader"
              />
            )}

            {showTasks() && (
              <>
                {state.tasks
                  ?.filter((_, index: number) =>
                    isTaskInCurrentPage(index, state.currentPage)
                  )
                  .map((task: Task, index: number) => {
                    return (
                      <RenderTask
                        key={`${task.title}-${index}`}
                        task={task}
                        navigateToExternalTask={navigateToExternalTask}
                        handleKeyPressFs={handleKeyPressFs}
                      />
                    );
                  })}
                {state.taskCount > pageSize && (
                  <WmPagination
                    currentPage={state.currentPage}
                    itemsPerPage={pageSize}
                    totalItems={state.taskCount}
                    onWmPaginationPageClicked={setPage}
                  ></WmPagination>
                )}
              </>
            )}
          </div>
        </WmTabPanel>
        <WmTabPanel className="tab-panel" id="tab-two" tabId={"completed"}>
          <div className="tab-container">
            <CompletedTasks />
          </div>
        </WmTabPanel>
      </div>
    </div>
  );
}

function ToDoEmptyState() {
  return (
    <div className="todo-empty-state">
      {translate("app.home.toDoEmptyState")}
    </div>
  );
}

const queryString = window.location.search;

interface RenderTaskProps {
  task: Task;
  navigateToExternalTask: (url: string) => void;
  handleKeyPressFs: (
    event: React.KeyboardEvent<HTMLDivElement>,
    url: string
  ) => void;
}

const formatCSTaskDetails = (title: string, state: string) => {
  switch (state.toLowerCase()) {
    case "draft":
      return [
        useIntl().formatMessage(
          { id: "app.home.csSubmitTaskTitle" },
          { title: title }
        ),
        useIntl().formatMessage({ id: "app.home.csSubmitTaskDesc" })
      ];
    default:
      return [
        useIntl().formatMessage(
          { id: "app.home.csResubmitTaskTitle" },
          { title: title }
        ),
        useIntl().formatMessage({ id: "app.home.csResubmitTaskDesc" })
      ];
  }
};

const formatInsightTaskDetails = (state: string) => {
  switch (state.toLowerCase()) {
    case "share_access_to_institution":
      return [
        useIntl().formatMessage({ id: "app.home.insightsInstitution" }),
        useIntl().formatMessage({ id: "app.home.insightsInstitutionDesc" })
      ];

    case "share_access_to_program":
      return [
        useIntl().formatMessage({ id: "app.home.insightsProgram" }),
        useIntl().formatMessage({ id: "app.home.insightsProgramDesc" })
      ];

    case "reporting_year_task":
      return [
        useIntl().formatMessage({ id: "app.home.createReportingYear" }),
        useIntl().formatMessage({ id: "app.home.createReportingYearDesc" })
      ];

    default:
      return ["", ""];
  }
};

const reviewerTaskTitle = (count: number, title: string) => {
  return [
    useIntl().formatMessage(
      { id: "app.home.fsReviewerTaskTitle" },
      { count: count, title: title }
    ),
    useIntl().formatMessage({ id: "app.home.fsReviewerTaskDesc" })
  ];
}

const fsTaskTitle = (title: string) => {
  return [
    useIntl().formatMessage(
      { id: "app.home.fsTaskTitle" },
      { title: title }
    ),
    useIntl().formatMessage({ id: "app.home.fsTaskDesc" }, { title: title })
  ];
}

const formatFSTaskDetails = (title: string, state: string, count: number) => {
  switch (state.toLowerCase()) {
    case "fs_reviewer_tasks":
      if (count > 1) {
        return reviewerTaskTitle(count, title);
      } else {
        return fsTaskTitle(title);
      }
    case "fs_tasks":
      return fsTaskTitle(title);
    default:
      return ["", ""];
  }
};

const formatTaskDetails = (product: string, title: string, state: string, count: number) => {
  switch (product.toLowerCase()) {
    case "curriculum strategy":
      return formatCSTaskDetails(title, state);
    case "faculty success":
      return formatFSTaskDetails(title, state, count);
    case "insights":
      return formatInsightTaskDetails(state);
    default:
      return ["", ""];
  }
};

function RenderTask({
  task,
  navigateToExternalTask,
  handleKeyPressFs
}: RenderTaskProps) {
  return task.product == "Insights" ? (
    <InsightsTask task={task} />
  ) : (
    <ExternalTask
      task={task}
      navigateToExternalTask={navigateToExternalTask}
      handleKeyPressFs={handleKeyPressFs}
    />
  );
}

function ExternalTask({
  task,
  navigateToExternalTask,
  handleKeyPressFs
}: RenderTaskProps) {
  const [taskTitle, taskDescription] = formatTaskDetails(
    task.product,
    task.title,
    task.state,
    task.count
  );

  const dueDate = useIntl().formatMessage(
    { id: "app.home.taskDueDate" },
    { date: formatDateWithMonthAbbr(task.dueDate) }
  );

  const lastModifyDate = useIntl().formatMessage(
    { id: "app.home.taskLastUpdate" },
    { date: formatDateWithMonthAbbr(task.lastModifyDate) }
  );
  return (
    <div
      className="task-container"
      onClick={() => navigateToExternalTask(task.url)}
      onKeyDown={(event) => handleKeyPressFs(event, task.url)}
    >
      <div className="flex-row create-reporting-year-link">
        <FolderIcon color={"#575195"} />
        <a className="reporting-year-link" href={task.url}>
          <span className="read-more">{taskTitle}</span>
        </a>
      </div>
      <div className="task-content flex-row">
        <div className="task-description">
          {taskDescription}
          <span className="task-type"> {task.product}.</span>
        </div>
        {
          (task.state == "fs_reviewer_tasks" && task.count > 1)
            ? (<div className="task-due-date">{task.dueDate}</div>)
            : (<div className="task-due-date">{dueDate}</div>)
        }
        {task.lastModifyDate && (
          <div className="task-due-date">{lastModifyDate}</div>
        )}
      </div>
    </div>
  );
}

const getIcon = (state: string) => {
  if (state == "reporting_year_task") {
    return <SettingsIcon />;
  } else {
    return <UserGroupIcon />;
  }
};

function InsightsTask({ task }: { task: Task }) {
  const [taskTitle, taskDescription] = formatTaskDetails(
    task.product,
    task.title,
    task.state,
    task.count
  );

  const navigate = useNavigate();
  function navigateToUrl() {
    scrollToTop(".home-page-header");
    queryString.includes("locale")
      ? navigate(`${task.url}/${queryString}`)
      : navigate(task.url);
  }
  function handleKeyDownLink(event: React.KeyboardEvent<HTMLAnchorElement>) {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      navigateToUrl();
    }
  }

  return (
    <div
      className="task-container"
      onClick={navigateToUrl}
      onKeyDown={handleKeyDownLink}
    >
      <div className="flex-row create-reporting-year-link">
        {getIcon(task.state)}

        <Link
          to={
            queryString.includes("locale")
              ? `${task.url}/${queryString}`
              : task.url
          }
          className="reporting-year-link"
          onKeyDown={handleKeyDownLink}
        >
          <span className="read-more">{taskTitle}</span>
        </Link>
      </div>
      <div className="task-content flex-row">
        <div className="task-description">{taskDescription}</div>
      </div>
    </div>
  );
}
